import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Lock,
  Unlock,
  Award,
  Clock,
  BookOpen,
  ArrowRight,
} from 'lucide-react';
import type { ModuleProgress, ModuleStatus } from '../types';

interface ModuleProgressCardProps {
  progress: ModuleProgress;
  onClaimFullCertificate?: () => void;
}

function ModuleStatusIcon({ status }: { status: ModuleStatus['status'] }) {
  switch (status) {
    case 'completed':
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    case 'available':
      return <Unlock className="w-5 h-5 text-blue-500" />;
    case 'locked':
      return <Lock className="w-5 h-5 text-gray-400" />;
  }
}

function ModuleStatusBadge({ status }: { status: ModuleStatus['status'] }) {
  switch (status) {
    case 'completed':
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
          Fullført
        </span>
      );
    case 'available':
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
          Tilgjengelig
        </span>
      );
    case 'locked':
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
          Låst
        </span>
      );
  }
}

export function ModuleProgressCard({ progress, onClaimFullCertificate }: ModuleProgressCardProps) {
  const [expanded, setExpanded] = useState(false);

  const completedCount = progress.modules.filter(m => m.status === 'completed').length;
  const totalCount = progress.modules.length;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div
        className="p-4 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">{progress.study_plan_title}</h3>
            <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Award className="w-4 h-4" />
                {progress.total_ects} ECTS
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {progress.total_hours} timer
              </span>
              <span className="flex items-center gap-1">
                <BookOpen className="w-4 h-4" />
                {completedCount}/{totalCount} moduler
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Progress circle */}
            <div className="relative w-12 h-12">
              <svg className="w-12 h-12 transform -rotate-90">
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  stroke="#e5e7eb"
                  strokeWidth="4"
                  fill="none"
                />
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  stroke="#22c55e"
                  strokeWidth="4"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 20}`}
                  strokeDashoffset={`${2 * Math.PI * 20 * (1 - progress.progress_percentage / 100)}`}
                  strokeLinecap="round"
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold">
                {Math.round(progress.progress_percentage)}%
              </span>
            </div>

            <button className="text-gray-400 hover:text-gray-600">
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-3">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 rounded-full transition-all duration-300"
              style={{ width: `${progress.progress_percentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Expanded module list */}
      {expanded && (
        <div className="border-t border-gray-100">
          <div className="p-4 space-y-3">
            {progress.modules.map((module, index) => (
              <div
                key={module.module_id}
                className={`p-3 rounded-lg border ${
                  module.status === 'completed'
                    ? 'bg-green-50 border-green-200'
                    : module.status === 'available'
                    ? 'bg-blue-50 border-blue-200'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <ModuleStatusIcon status={module.status} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs bg-gray-200 px-1.5 py-0.5 rounded">
                        {module.module_code}
                      </span>
                      <span className="font-medium text-gray-900">{module.title}</span>
                      <ModuleStatusBadge status={module.status} />
                    </div>

                    {module.description && (
                      <p className="mt-1 text-sm text-gray-600">{module.description}</p>
                    )}

                    <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                      <span>{module.ects_credits} ECTS</span>
                      <span>{module.estimated_hours} timer</span>
                      {module.is_standalone && (
                        <span className="text-green-600">Kan tas selvstendig</span>
                      )}
                    </div>

                    {/* Prerequisites */}
                    {module.prerequisites.length > 0 && (
                      <div className="mt-2 text-xs text-gray-500">
                        <span className="font-medium">Forkunnskaper:</span>{' '}
                        {module.prerequisites.join(', ')}
                      </div>
                    )}

                    {/* Link to certificate if completed */}
                    {module.status === 'completed' && module.certificate_id && (
                      <div className="mt-2">
                        <Link
                          to={`/certificates/${module.certificate_id}`}
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                        >
                          Se attest <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* Module number */}
                  <div className="flex-shrink-0 text-2xl font-bold text-gray-200">
                    {index + 1}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Full certificate claim button */}
          {progress.can_claim_full_certificate && onClaimFullCertificate && (
            <div className="p-4 bg-green-50 border-t border-green-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-green-800">
                    Gratulerer! Du har fullført alle moduler.
                  </p>
                  <p className="text-sm text-green-600">
                    Du kan nå hente ut en samlet kompetanseattest for hele studieplanen.
                  </p>
                </div>
                <button
                  onClick={onClaimFullCertificate}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center gap-2"
                >
                  <Award className="w-5 h-5" />
                  Hent samlet attest
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
